import axios, { AxiosHeaders, type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { toApiError } from '@/lib/apiProblem';
import { env } from '@/lib/env';
import { refreshAccessToken } from '@/lib/tokenRefresh';
import { useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';

/**
 * The single HTTP client of the application.
 *
 * Behaviour that every caller relies on:
 * - the address is the one the deployment configured (TASK-0076), read from `env` — never a copy taken from the bundle;
 * - the access token is attached from the in-memory store (never from `localStorage`, ADR-0011);
 * - `Accept-Language` follows the active locale, so the API localizes problem titles and details;
 * - credentials are sent, so the `HttpOnly` refresh cookie reaches `/auth/refresh`;
 * - a `401 token_expired` triggers exactly one refresh and one replay of the original request;
 * - every failure is normalized into an `ApiError` carrying the RFC 7807 problem document.
 */

/** Marks a request that has already been replayed once, so a refresh loop is impossible. */
interface ReplayableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

/** Endpoints whose own 401 must reach the caller instead of starting a refresh cycle. */
const AUTH_ENDPOINTS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/logout',
  '/auth/register',
  '/auth/verify-email',
  '/auth/reset-password',
];

/** Returns the language tag the API expects for localized problem documents. */
export function acceptLanguageFor(locale: string): string {
  return locale === 'en' ? 'en-US,en;q=0.9' : 'fa-IR,fa;q=0.9,en;q=0.5';
}

/** Determines whether a request targets an auth endpoint that must not be auto-refreshed. */
export function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

export const apiClient = axios.create({
  // Correct at import time for every caller that runs after `configureRuntime`; the request interceptor below keeps it
  // correct if the configuration is ever applied again (a test, or a deployment that reloads its file).
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeoutMs,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use((config: ReplayableConfig) => {
  // The deployed address and timeout are authoritative. Axios resolved `baseURL` when the instance was created, which is
  // before any runtime file could have been read, so the two are synchronised here — the single place every request
  // passes through — instead of asking every caller to know about it.
  if (config.baseURL !== env.apiBaseUrl) {
    config.baseURL = env.apiBaseUrl;
  }

  if (config.timeout !== env.apiTimeoutMs) {
    config.timeout = env.apiTimeoutMs;
  }

  const { accessToken } = useAuthStore.getState();

  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  if (!config.headers.has('Accept-Language')) {
    config.headers.set('Accept-Language', acceptLanguageFor(useUiStore.getState().locale));
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as ReplayableConfig | undefined;
    const status = error.response?.status;
    const shouldRetry =
      status === 401 &&
      config !== undefined &&
      config._retried !== true &&
      !isAuthEndpoint(config.url);

    if (!shouldRetry) {
      throw toApiError(error);
    }

    const session = await refreshAccessToken();

    if (!session) {
      useAuthStore.getState().clear();
      throw toApiError(error);
    }

    config._retried = true;
    config.headers = AxiosHeaders.from(config.headers);
    config.headers.set('Authorization', `Bearer ${session.accessToken}`);

    return apiClient.request(config);
  },
);
